import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Apollo } from 'apollo-angular';

import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import { errorMessageParser, ShowError } from '@ui/legacy-lib';
import { BrandingTheme, SimpleCustomerBranding } from '@ui/shared/model';

import {
  customerBrandingQuery,
  landlordBrandingQuery
} from '../../core/queries/branding.queries';
import { notificationConfig } from '../../config/notification.config';
import {
  LOAD_CUSTOMER_BRANDING,
  LoadCustomerBranding,
  LoadCustomerBrandingFail,
  LoadCustomerBrandingSuccess,
  VALIDATE_THEME_TOKEN,
  ValidateThemeToken,
  ValidateThemeTokenFail,
  ValidateThemeTokenSuccess
} from './branding.actions';

export interface BrandingResponse {
  landlordBranding: BrandingTheme;
}

export interface CustomerBrandingResponse {
  customerBranding: SimpleCustomerBranding;
}

@Injectable()
export class BrandingEffects {
  private actions$ = inject(Actions);
  private apollo = inject(Apollo);

  validateToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ValidateThemeToken>(VALIDATE_THEME_TOKEN),
      switchMap(({ token, preview }) =>
        this.apollo
          .query({
            query: landlordBrandingQuery,
            variables: {
              input: {
                token,
                preview
              }
            }
          })
          .pipe(
            map(
              theme =>
                new ValidateThemeTokenSuccess(theme.data.landlordBranding)
            ),
            catchError(_err =>
              of(
                new ValidateThemeTokenFail(
                  notificationConfig.branding.validate.error
                )
              )
            )
          )
      )
    )
  );

  customerBranding$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCustomerBranding>(LOAD_CUSTOMER_BRANDING),
      switchMap(({ customerId }) =>
        this.apollo
          .query({
            query: customerBrandingQuery,
            variables: {
              customerId
            }
          })
          .pipe(
            map(
              response =>
                new LoadCustomerBrandingSuccess(response.data.customerBranding)
            ),
            catchError((err: Error) => {
              return [
                new LoadCustomerBrandingFail(err),
                new ShowError(errorMessageParser(err))
              ];
            })
          )
      )
    )
  );
}
